import {
  Button,
  FormRow,
  Input,
  NumberStepper,
  Select,
  Textarea,
} from "@/components/common";
import SelectConditionFlow from "@/components/common/select/condition-select/select-condition-flow";
import { TParcelServices } from "@/types/parcel.types";
import { Field, useFormikContext } from "formik";
import { useTranslations } from "next-intl";
import { useState } from "react";

const ParcelDividerModalUa = ({
  handleAddInfo,
}: {
  handleAddInfo: () => void;
}) => {
  const t = useTranslations();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const {
    values,
    setFieldValue,
    getFieldMeta,
    setFieldTouched,
    errors,
    setFieldError,
  } = useFormikContext<TParcelServices>();
  const { serviceDivideInformation } = values;
  const [serviceDevideQuantity, setServiceDevideQuantity] = useState(
    serviceDivideInformation?.length || 2,
  );

  const updateServiceDivideField = (
    index: number,
    partial: Partial<
      NonNullable<TParcelServices["serviceDivideInformation"]>[number]
    > & {
      priceUsd?: string | number;
      brandName?: string;
      modelName?: string;
      description?: string;
      productCondition?: any;
      conditionId?: number;
    },
  ) => {
    const current = serviceDivideInformation ?? [];
    if (current.length > 0) {
      if (index > current.length - 1) {
        const addedNew = [...current, partial];
        setFieldValue("serviceDivideInformation", addedNew);
      } else {
        const updated = current.map((info, i) =>
          i === index ? { ...info, ...partial } : info,
        );
        setFieldValue("serviceDivideInformation", updated);
      }
    } else {
      setFieldValue("serviceDivideInformation", [partial]);
    }
  };

  return (
    <>
      <FormRow>
        <NumberStepper
          label="quantity"
          value={serviceDevideQuantity}
          onNumberChange={(value: string) => {
            setServiceDevideQuantity(parseFloat(value));
          }}
          required
        />
      </FormRow>
      {Array.from(Array(serviceDevideQuantity).keys())?.map(
        (splitFormIndex) => {
          return (
            <FormRow
              key={splitFormIndex}
              translateLabel={false}
              rowLabel={`${t("split")} #${splitFormIndex + 1}`}
            >
              <Field
                name={`serviceDivideInformation.${splitFormIndex}.priceUsd`}
              >
                {({ field, meta }) => (
                  <FormRow className="w-full">
                    <Input
                      label="price"
                      error={
                        isSubmitted ? meta.error : meta.touched && meta.error
                      }
                      {...field}
                      onChange={(e) => {
                        const inputValue = e.target.value;

                        // Allow valid numeric input, including decimals in progress (e.g., "2.", "2.2")
                        if (!/^\d*\.?\d*$/.test(inputValue)) {
                          return; // Prevent invalid characters
                        }

                        const parsedValue =
                          inputValue === "." ? null : parseFloat(inputValue);

                        // Preserve original behavior: store raw string in form state
                        updateServiceDivideField(splitFormIndex, {
                          priceUsd: inputValue,
                        });
                      }}
                      currency="$"
                      wrapperClassName="mt-[14px]"
                    />
                  </FormRow>
                )}
              </Field>
              <Field
                name={`serviceDivideInformation.${splitFormIndex}.description`}
              >
                {({ field, meta }) => (
                  <FormRow className="w-full">
                    <Textarea
                      name="description"
                      label="add_comment"
                      resize="resize-none"
                      className="h-[9.375rem] !rounded-[2.25rem] !px-[2rem] !text-base font-normal"
                      containerClassName="mt-[14px]"
                      labelClassName="!left-[2rem]"
                      error={
                        isSubmitted ? meta.error : meta.touched && meta.error
                      }
                      {...field}
                      onChange={(e) => {
                        updateServiceDivideField(splitFormIndex, {
                          description: (e.target as HTMLTextAreaElement).value,
                        });
                      }}
                    />
                  </FormRow>
                )}
              </Field>
              <div className="mt-[15px]">
                <Field
                  name={`serviceDivideInformation.${splitFormIndex}.brandName`}
                >
                  {({ field, meta }) => (
                    <Input
                      label={"brand"}
                      error={
                        isSubmitted ? meta.error : meta.touched && meta.error
                      }
                      {...field}
                      onChange={(e) => {
                        updateServiceDivideField(splitFormIndex, {
                          brandName: e.target.value,
                        });
                      }}
                    />
                  )}
                </Field>
              </div>
              <div className="mt-[19px]">
                <Field
                  name={`serviceDivideInformation.${splitFormIndex}.modelName`}
                >
                  {({ field, meta }) => (
                    <Input
                      label={"model"}
                      error={
                        isSubmitted ? meta.error : meta.touched && meta.error
                      }
                      {...field}
                      onChange={(e) => {
                        updateServiceDivideField(splitFormIndex, {
                          modelName: e.target.value,
                        });
                      }}
                    />
                  )}
                </Field>
              </div>
              <div className="mt-[19px]">
                <Select
                  label={"product_condition"}
                  selectedValue={
                    serviceDivideInformation?.[splitFormIndex]?.productCondition
                  }
                  name={`serviceDivideInformation.${splitFormIndex}.productCondition`}
                  iconFit="scale-down"
                  error={
                    isSubmitted
                      ? getFieldMeta(
                          `serviceDivideInformation.${splitFormIndex}.productCondition`,
                        ).error
                      : getFieldMeta(
                          `serviceDivideInformation.${splitFormIndex}.productCondition`,
                        ).touched &&
                        getFieldMeta(
                          `serviceDivideInformation.${splitFormIndex}.productCondition`,
                        ).error
                  }
                  handleTouch={() =>
                    setFieldTouched(
                      `serviceDivideInformation.${splitFormIndex}.productCondition`,
                    )
                  }
                  translation={false}
                >
                  <SelectConditionFlow
                    onSelect={(value) => {
                      // value: { id, mainText, ... }
                      // Single source of truth: push full option + numeric id
                      updateServiceDivideField(splitFormIndex, {
                        productCondition: value,
                        conditionId: value.id === "used" ? 1 : 2,
                      });
                    }}
                  />
                </Select>
              </div>
            </FormRow>
          );
        },
      )}
      <div className="sticky bottom-0 border-t-[1px] border-[#F4F4F4] bg-white py-[30px]">
        <Button
          type="button"
          className="capitalize"
          onClick={() => {
            setIsSubmitted(true);
            handleAddInfo();
          }}
        >
          {t("save")}
        </Button>
      </div>
    </>
  );
};

export default ParcelDividerModalUa;
